import React, { useRef } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Button, Heading, IconButton, Paragraph, Popover, Table, Text } from '@zillow/constellation-10';
import { IconInfoFilled } from '@zillow/constellation-icons';

import { AccountViewListingStatus, PropertyType } from '@zg-rentals/ts-rental-manager-api-client';
import { LoadingParagraph } from '@zg-rentals/loading-components';

import { useGetPropertiesData } from '../../../../hooks/queries/zram';
import { useTableObserver } from '../../../../hooks/useTableObserver';

import { useSelector } from '../../../../selectors/useSelector';
import { AppSelectors } from '../../../../selectors';

import { ZRMOverview } from '../../../../components/properties/ZRMOverview';
import { NoSearchResults } from '../../../../components/properties/NoSearchResults';
import ClaimYourListingDetails from '../../../../components/ClaimYourListingDetails';
import { PropertyRow } from '../PropertyRow';
import { FixedScrollbar } from './FixedScrollbar';

import PropertiesConstants from '../../../../constants/PropertiesConstants';

import type { AccountViewListingDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FilterState } from '../..';

import { errorContainerStyles, stickyLeftCellRecipe, StyledHeaderCell, tableWrapperStyles } from '../styles';

const SKELETON_LISTING: AccountViewListingDto = {
  listingAlias: '',
  name: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    unit: '',
  },
  photoKey: '',
  propertyType: PropertyType.UNKNOWN,
  isMultifamily: false,
  status: {
    status: AccountViewListingStatus.PUBLISHED,
    text: 'Published',
    tagAppearance: 'success',
  },
  // TODO: remove once BE response is updated
  updatedMs: 0,
};

export const PropertyTable = ({
  filters,
  setFilters,
  isSkeletonView,
}: {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  isSkeletonView: boolean;
}) => {
  const { isScreenMobile, user } = useSelector(
    (state) => ({
      isScreenMobile: AppSelectors.makeIsScreenMobile(state),
      user: state.user,
    }),
    shallowEqual,
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { isScrolledToLeft, showFixedScrollbar, isTableBottomVisible } = useTableObserver({
    scrollContainerRef,
    isScreenMobile,
  });

  const {
    data: { listings = [], listingCount } = {},
    isError,
    isFetching: isPropertiesFetching,
    isFetchingNextPage: isFetchingNextPage,
  } = useGetPropertiesData(filters);

  const { archived: numArchivedListings = 0 } = listingCount || {};

  const isArchivedView = filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED;

  const isPageLoading = (isPropertiesFetching && !isFetchingNextPage) || isSkeletonView;
  const listingsToRender = isPageLoading
    ? Array(PropertiesConstants.PROPERTIES_LIMIT_DEFAULT).fill(SKELETON_LISTING)
    : listings;

  const renderError = () => (
    <Box css={errorContainerStyles}>
      We are having trouble retrieving your properties at this time. Please refresh the page or check back soon.
    </Box>
  );

  const renderInfoPopover = (header: string, body: string) => (
    <Popover
      body={<Paragraph>{body}</Paragraph>}
      trigger={
        <IconButton
          title={header}
          size="sm"
          emphasis="bare"
          shape="circle"
          css={{
            color: 'icon.muted',
            width: 'icon.sm',
            height: 'icon.sm',
            verticalAlign: 'text-top',
            marginLeft: '100',
          }}
          icon={<IconInfoFilled />}
          onClick={(e) => e.stopPropagation()}
        />
      }
      header={<Heading level={4}>{header}</Heading>}
    />
  );

  const renderNoListings = () => {
    if (filters.searchText) {
      return (
        <NoSearchResults
          showArchived={filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED}
          onRemoveSearchTextClick={() => {
            setFilters({ ...filters, searchText: '' });
          }}
        />
      );
    } else if (filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED) {
      return <Text>You don&apos;t have any archived properties.</Text>;
    } else if (filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED) {
      return (
        <Box css={{ textAlign: 'center' }}>
          <Heading level={2} textStyle="heading-md" fontFamily="heading-alt">
            Claim and list your properties for free
          </Heading>
          <Paragraph css={{ marginBlock: '600' }}>
            Help renters discover your unadvertised properties in just a few steps.
          </Paragraph>
          <Button tone="brand" emphasis="filled" asChild={true} css={{ marginBottom: '1200' }}>
            <a href="/rental-manager/ad-management/how-claiming-works">Get started</a>
          </Button>
          <ClaimYourListingDetails />
          <Paragraph css={{ marginTop: '1200' }}>
            Unclaimed properties with under 25 units may already be listed on Zillow, Trulia and HotPads.
          </Paragraph>
          <Paragraph>Claim yours to manage your portfolio in one place and move faster to fill vacancies.</Paragraph>
        </Box>
      );
    } else if (user.features.canAddListing) {
      return (
        <ZRMOverview
          hasAnyArchivedListings={numArchivedListings > 0}
          handleShowArchivedClick={() =>
            setFilters({ ...filters, subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED })
          }
        />
      );
    }
  };

  const renderTable = () => (
    <>
      {isArchivedView && (
        <LoadingParagraph css={{ marginTop: '600' }} isLoading={isPageLoading} loadingWidth="10rem">
          {numArchivedListings} {numArchivedListings > 1 ? 'properties' : 'property'}
        </LoadingParagraph>
      )}
      <Box css={tableWrapperStyles.raw({ shouldShowBorder: isScreenMobile && isScrolledToLeft })}>
        <Box
          ref={scrollContainerRef}
          css={{
            overflowX: { base: 'hidden', lg: 'auto' },
            maxWidth: '100%',
          }}
        >
          <Table.Root
            aria-label="Properties list"
            css={{
              minWidth: isArchivedView || user.isSingleFamilyEnterpriseCustomer ? '100%' : { lg: '1550px' },
            }}
            appearance="horizontal"
          >
            <Table.Header css={{ display: { base: 'none', lg: 'table-header-group' } }}>
              <Table.Row>
                <StyledHeaderCell css={stickyLeftCellRecipe.raw({ scrolled: isScrolledToLeft })}>
                  Property address
                </StyledHeaderCell>
                <StyledHeaderCell>Status</StyledHeaderCell>
                {!isArchivedView && (
                  <>
                    {!user.isSingleFamilyEnterpriseCustomer && (
                      <>
                        <StyledHeaderCell>
                          <Paragraph textStyle="body-sm-bold" css={{ minWidth: 'max-content' }}>
                            Listing quality score
                            {renderInfoPopover(
                              'Listing quality score',
                              'Score reflects unit-level availability and completeness of key property details, weighted using Zillow internal data.',
                            )}
                          </Paragraph>
                        </StyledHeaderCell>
                        <StyledHeaderCell>
                          <Paragraph textStyle="body-sm-bold" css={{ minWidth: 'max-content' }}>
                            Missing details
                            {renderInfoPopover(
                              'Missing details',
                              'Top recommended details to add, based on Zillow internal data. These details are what renters most care about.',
                            )}
                          </Paragraph>
                        </StyledHeaderCell>
                      </>
                    )}
                    <StyledHeaderCell>
                      <Paragraph textStyle="body-sm-bold" css={{ minWidth: 'max-content' }}>
                        Prospects
                        {renderInfoPopover(
                          'Total prospects',
                          'Includes phone leads, email leads, and website referrals.',
                        )}
                      </Paragraph>
                    </StyledHeaderCell>
                    <StyledHeaderCell>Advertising</StyledHeaderCell>
                  </>
                )}
                <StyledHeaderCell>Source</StyledHeaderCell>
                {/* Placeholder for actions column */}
                <StyledHeaderCell> </StyledHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {listingsToRender.map((listing, index) => (
                <PropertyRow
                  key={isPageLoading ? `skeleton-${index}` : listing?.listingAlias}
                  listing={listing}
                  isLoading={isPageLoading}
                  isScrolledToLeft={isScrolledToLeft}
                  isArchivedView={isArchivedView}
                />
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>
      <FixedScrollbar
        scrollContainerRef={scrollContainerRef}
        isVisible={showFixedScrollbar}
        isTableBottomVisible={isTableBottomVisible}
      />
    </>
  );

  return (
    <>
      {isError && renderError()}
      {!isError && !isPageLoading && listingsToRender.length === 0 && (
        <Box css={errorContainerStyles}>{renderNoListings()}</Box>
      )}
      {!isError && listingsToRender.length > 0 && renderTable()}
    </>
  );
};
